// Light rendering utilities

/** @typedef {import('../types/jsdoc-types.js').Light} Light */

import { drawFlameIcon } from './icon-rendering.js';

/**
 * Draw all lights with selection/hover highlighting
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<Light>} lights
 * @param {object} options
 * @param {number} options.gridSize
 * @param {number} options.zoom
 * @param {string} options.tool - Current editor tool
 * @param {number|null} [options.selectedLightIndex=null]
 * @param {number|null} [options.hoveredLightIndex=null]
 * @param {{x: number, y: number}|null} [options.lightPreviewPoint=null] - Light preview point
 * @param {string} [options.lightSubtool='selectLight'] - Current light subtool
 */
export function drawLights(ctx, lights, options) {
  const {
    gridSize,
    zoom,
    tool,
    selectedLightIndex = null,
    hoveredLightIndex = null,
    lightPreviewPoint = null,
    lightSubtool = 'selectLight',
  } = options;

  const isLightTool = tool === 'drawLight';

  for (let i = 0; i < lights.length; i++) {
    const light = lights[i];
    if (!light) continue;

    // Resolve position: light may use col/row directly or position.col/position.row
    const col = light.col ?? light.position?.col;
    const row = light.row ?? light.position?.row;
    if (col === undefined || row === undefined) continue;

    const isSelected = i === selectedLightIndex;
    const isHovered = i === hoveredLightIndex;

    // Pixel coordinates for center of cell
    const lightX = col * gridSize + gridSize / 2;
    const lightY = row * gridSize + gridSize / 2;

    // Draw range indicators for selected light (when in light tool)
    if (isSelected && isLightTool) {
      ctx.setLineDash([20, 10]);
      ctx.lineWidth = 6 / zoom;
      ctx.lineCap = 'round';

      // Draw dim radius outline (orange dotted)
      const totalRadius = light.brightRadius + light.dimRadius;
      const dimSize = totalRadius * gridSize;
      ctx.strokeStyle = '#ffa500';
      ctx.strokeRect(
        lightX - dimSize - gridSize / 2,
        lightY - dimSize - gridSize / 2,
        dimSize * 2 + gridSize,
        dimSize * 2 + gridSize,
      );

      // Draw bright radius outline (bright yellow dotted)
      const brightSize = light.brightRadius * gridSize;
      ctx.strokeStyle = '#ffdd00';
      ctx.strokeRect(
        lightX - brightSize - gridSize / 2,
        lightY - brightSize - gridSize / 2,
        brightSize * 2 + gridSize,
        brightSize * 2 + gridSize,
      );

      ctx.setLineDash([]);
      ctx.lineCap = 'butt';
    }

    // Draw flame icon
    const isHoveredInSelectMode = isHovered && isLightTool;
    const isOn = light.isOn !== false; // Default to on
    drawFlameIcon(ctx, lightX, lightY, gridSize, zoom, isOn, isSelected, isHoveredInSelectMode);
  }

  // Draw preview point when in drawLight subtool
  if (isLightTool && lightSubtool === 'drawLight' && lightPreviewPoint) {
    // Draw dashed range indicator for default radii (brightRadius=4, dimRadius=4)
    const defaultBrightRadius = 4;
    const defaultDimRadius = 4;
    const previewX = lightPreviewPoint.x;
    const previewY = lightPreviewPoint.y;

    ctx.save();
    ctx.globalAlpha = 0.4;
    ctx.setLineDash([20, 10]);
    ctx.lineWidth = 6 / zoom;
    ctx.lineCap = 'round';

    // Draw dim radius outline (orange dotted)
    const totalRadius = defaultBrightRadius + defaultDimRadius;
    const dimSize = totalRadius * gridSize;
    ctx.strokeStyle = '#ffa500';
    ctx.strokeRect(
      previewX - dimSize - gridSize / 2,
      previewY - dimSize - gridSize / 2,
      dimSize * 2 + gridSize,
      dimSize * 2 + gridSize,
    );

    // Draw bright radius outline (bright yellow dotted)
    const brightSize = defaultBrightRadius * gridSize;
    ctx.strokeStyle = '#ffdd00';
    ctx.strokeRect(
      previewX - brightSize - gridSize / 2,
      previewY - brightSize - gridSize / 2,
      brightSize * 2 + gridSize,
      brightSize * 2 + gridSize,
    );

    ctx.setLineDash([]);
    ctx.lineCap = 'butt';
    ctx.restore();

    // Draw semi-transparent flame icon at preview position
    ctx.save();
    ctx.globalAlpha = 0.5;
    drawFlameIcon(ctx, previewX, previewY, gridSize, zoom, true, false, false);
    ctx.restore();
  }
}
